// The one rule for "has the vendor published a rework of a sent-back answer".
// Lives in @l2u/shared because BOTH sides need it and they cannot share
// @l2u/ui: the API is a Worker with no React dependency. Every other home for
// this rule ended up hand-copied — see the note below.

export type ReworkFields = {
  /** When the vendor published a rework of a sent-back answer, by replying on
   *  the thread. Cleared by the admin's next verdict (reviewPublish). */
  resubmitted_at?: string | null;
  /** The admin's last response — a verdict publish or a standalone comment. A
   *  vendor edit nulls it while keeping status 'rejected'. */
  reviewed_at?: string | null;
};

/**
 * Whether the vendor has published a rework the admin has not responded to yet.
 *
 * This is the signal that replaced the "Submit to admin" button. That route
 * updated `{ status: 'pending', resubmitted_at: now }`; the thread reply that
 * replaced it stamps only `resubmitted_at`, because a sent-back answer keeps
 * status 'rejected' for the whole round — that status is what keeps its inputs
 * editable while the vendor reworks it. So `status === 'rejected'` on its own
 * does NOT mean the vendor still owes a response, and every "whose turn is it"
 * predicate has to consult this one: the vendor's action badges, the admin's
 * "Reply needed" row badge, and the company status that labels the admin's
 * queue ('changes' = "Vendor change requested", 'review' = "Action required").
 *
 * Any `reviewed_at` newer than the stamp resolves it (a verdict OR a standalone
 * admin comment); verdicts also null `resubmitted_at` outright.
 *
 * Four call sites share this: isAwaitingVendor / outstandingActionCount
 * (packages/ui), awaitingAdminReply (apps/admin), and isSubmissionAwaitingVendor
 * (apps/api reviewRollup, which decides the company status). It was hand-copied
 * into three of them, each with a comment promising to keep them in sync — which
 * is exactly how removing the "Submit to admin" button updated two and left the
 * rest reading a status that no longer moves. Import it; never restate it.
 */
export function hasPublishedRework(sub: ReworkFields): boolean {
  if (sub.resubmitted_at == null) return false;
  return sub.reviewed_at == null || sub.reviewed_at < sub.resubmitted_at;
}
